"use client";

import React from "react";
import { Phone, Mail, CircleCheck as CheckCircle2 } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const contactDetails = [{
  icon: Phone,
  label: "Phone consultation",
  value: "(470) 847-9076",
  to: "tel:+14708479076",
  subtext: "We will follow up to discuss your project."
}, {
  icon: Mail,
  label: "Direct email",
  value: "serviceleadalerts@gmail.com",
  to: "mailto:serviceleadalerts@gmail.com",
  subtext: "Detailed remodel inquiries"
}];
const communities = [{
  name: "Historic Brookhaven",
  postal: "GA 30319"
}, {
  name: "Ashford Park",
  postal: "GA 30319"
}, {
  name: "Brittany",
  postal: "GA 30319"
}, {
  name: "Murphey Candler",
  postal: "GA 30319"
}, {
  name: "Dunwoody",
  postal: "GA 30338"
}, {
  name: "Sandy Springs",
  postal: "GA 30328"
}, {
  name: "Buckhead",
  postal: "GA 30326"
}, {
  name: "Chamblee",
  postal: "GA 30341"
}];
export default function Visit() {
  const shouldReduceMotion = useReducedMotion();
  const revealMotion = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    whileInView: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 15
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="visit-service-area" data-nav="light" className="relative w-full bg-background py-16 md:py-24 text-foreground selection:bg-primary/20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left Column: Heading, Intro Copy, Direct Contacts */}
          <motion.div {...revealMotion} className="lg:col-span-6 flex flex-col justify-start">
            {/* Eyebrow Label */}
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="outline" className="rounded-lg border-primary/40 bg-primary/10 px-3.5 py-1 text-xs font-semibold tracking-wider text-foreground uppercase">
                Service territory &amp; inquiries
              </Badge>
              <div className="h-px w-10 bg-primary/40 hidden sm:block" />
            </div>

            {/* Headline */}
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.18] mb-4">
              Service territory &amp; consultations
            </h2>

            {/* Introductory copy */}
            <p className="text-base sm:text-lg text-foreground/80 leading-relaxed mb-8 max-w-xl">
              We provide comprehensive residential design and remodeling within a
              focused 15-mile corridor across Brookhaven, Buckhead, and North
              Atlanta communities. Reach us directly or request an in-home
              consultation.
            </p>

            {/* Contact Cards */}
            <div className="flex flex-col gap-4">
              {contactDetails.map((item, i) => {
              const IconComponent = item.icon;
              return <motion.div key={item.label} data-index={i} whileHover={shouldReduceMotion ? {} : {
                y: -4,
                transition: {
                  duration: 0.25,
                  ease: "easeOut"
                }
              }} className="group rounded-2xl border border-white/80 bg-white/75 backdrop-blur-md p-5 sm:p-6 shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/50 hover:shadow-md">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                        <IconComponent className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block mb-1">
                          {item.label}
                        </span>
                        <Link to={item.to} className="font-medium text-foreground hover:text-primary transition-colors text-base sm:text-lg break-words block">
                          {item.value}
                        </Link>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                          {item.subtext}
                        </p>
                      </div>
                    </div>
                  </motion.div>;
            })}
            </div>
          </motion.div>

          {/* Right Column: Served Communities Grid */}
          <motion.div {...revealMotion} className="lg:col-span-6 flex flex-col justify-start">
            <div className="rounded-2xl border border-border/80 bg-card/70 backdrop-blur-md p-6 sm:p-8 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-6 pb-4 border-b border-border/60">
                <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
                  Primary residential districts served
                </h3>
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  North Atlanta Metro
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {communities.map((comm, i) => <div key={comm.name} data-index={i} className="flex items-center gap-3 rounded-lg border border-border/60 bg-background/80 px-3.5 py-3 shadow-2xs">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-foreground block truncate">
                        {comm.name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {comm.postal}
                      </span>
                    </div>
                  </div>)}
              </div>

              <p className="text-xs text-muted-foreground mt-6 leading-relaxed">
                Remodeling services are tailored to each neighborhood&apos;s architectural guidelines and residential zoning standards.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}